import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Switch,
  Grid,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  IconButton,
  Tooltip,
  Image,
  Divider,
  SimpleGrid,
  CloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { 
  AddIcon, 
  EditIcon, 
  DeleteIcon, 
  CopyIcon, 
  RepeatIcon, 
  ExternalLinkIcon,
  SettingsIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DragHandleIcon,
  SunIcon,
  MoonIcon,
  CopyIcon as CloneIcon,
  LinkIcon,
} from '@chakra-ui/icons';
import { Config, Collection, CollectionFormData } from '../../types/config';
import { ConfigService } from '../../services/configService';
import { CollectionService } from '../../services/collectionService';
import { useTheme } from '../../hooks/useTheme';
import GitLabService from '../../services/gitlabService';
import { UserService, User } from '../../services/userService';

export const Settings: React.FC = () => {
  const [configs, setConfigs] = useState<Config[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<Config | null>(null);
  const [editingConfig, setEditingConfig] = useState<Config | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [gitlabToken, setGitlabToken] = useState('');
  const [gitlabTokenSaved, setGitlabTokenSaved] = useState(false);
  const [editingGitlabToken, setEditingGitlabToken] = useState(false);
  const [defaultGitlabUrl, setDefaultGitlabUrl] = useState('');
  const [defaultGitlabUrlSaved, setDefaultGitlabUrlSaved] = useState(false);
  const [editingDefaultGitlabUrl, setEditingDefaultGitlabUrl] = useState(false);
  
  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<{ username: string; email: string; password: string; is_admin: boolean }>({ username: '', email: '', password: '', is_admin: false });
  const [userFormError, setUserFormError] = useState('');
  const [userFormSaving, setUserFormSaving] = useState(false);
  
  // User-Collection access state
  const [usersWithAccess, setUsersWithAccess] = useState<User[]>([]);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<User | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessUpdating, setAccessUpdating] = useState<{[key: string]: boolean}>({});

  // Collections management state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [collectionForm, setCollectionForm] = useState<CollectionFormData>({ name: '', description: '', isDefault: false, documentIds: [] });
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [collectionFormError, setCollectionFormError] = useState('');
  const [collectionFormSaving, setCollectionFormSaving] = useState(false);

  const { colorMode, toggleColorMode, themeConfig } = useTheme();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use theme-aware color values that respect the selected color scheme
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue(`${themeConfig.colorScheme}.50`, `${themeConfig.colorScheme}.900`);
  const activeBg = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    loadConfigs();
    loadGitlabToken();
    loadDefaultGitlabUrl();
    loadUsers(); // Load users on mount
    loadCollections(); // Load collections on mount
    loadUsersWithAccess(); // Load user-collection access data
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const loadedConfigs = await ConfigService.fetchConfigs();
      setConfigs(loadedConfigs.sort((a: Config, b: Config) => (a.order || 0) - (b.order || 0)));
      
      loadCollections();
      
      // Select first config by default AND enter edit mode immediately (Angular behavior)
      if (loadedConfigs.length > 0 && !selectedConfig) {
        const firstConfig = loadedConfigs[0];
        setSelectedConfig(firstConfig);
        setEditingConfig({ ...firstConfig });
        setIsEditMode(true);
        setIconPreview(firstConfig.icon || null);
      }
    } catch (error: any) {
      setErrorMessage('Failed to load configurations. Please check if the backend server is running.');
      console.error('Error loading configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateId = (): string => {
    return 'doc-' + Math.random().toString(36).substr(2, 9);
  };

  const addNewDocument = () => {
    const newConfig: Config = {
      id: generateId(),
      name: '',
      description: '',
      apiKey: '',
      endpointUrl: '',
      parametersUrl: '',
      requestUrl: '',
      cueText: '',
      icon: '',
      isActive: configs.length === 0, // First config is active by default
      isEnabled: true, // New configs are enabled by default
      parameters: [],
      lastEdited: Date.now(),
      order: configs.length,
      isShared: false,
    };
    
    setSelectedConfig(newConfig);
    setEditingConfig(newConfig);
    setIsEditMode(false);
    setIconPreview(null);
    setErrors({});
  };

  const editDocument = (config: Config) => {
    setSelectedConfig(config);
    setEditingConfig({ ...config });
    setIsEditMode(true);
    setIconPreview(config.icon || null);
    setErrors({});
  };

  const deleteDocument = async (config: Config) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setSaving(true);
      await ConfigService.deleteConfig(config.id);
      
      const updatedConfigs = configs.filter(c => c.id !== config.id);
      
      // If deleted config was active and there are other configs, make first one active
      if (config.isActive && updatedConfigs.length > 0) {
        updatedConfigs[0].isActive = true;
        await ConfigService.saveConfigs(updatedConfigs);
      }
      
      setConfigs(updatedConfigs);
      
      // Update selected config
      if (selectedConfig?.id === config.id) {
        setSelectedConfig(updatedConfigs.length > 0 ? updatedConfigs[0] : null);
      }
      
      setSuccessMessage('Document deleted successfully!');
    } catch (error: any) {
      setErrorMessage('Failed to delete document.');
      console.error('Error deleting config:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveDocument = async () => {
    if (!editingConfig) return;

    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (!editingConfig.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!editingConfig.apiKey.trim()) {
      newErrors.apiKey = 'API Key is required';
    }
    if (!editingConfig.endpointUrl.trim()) {
      newErrors.endpointUrl = 'Endpoint URL is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);
      
      // Add protocol if missing
      const configToSave = {
        ...editingConfig,
        endpointUrl: addProtocolToUrl(editingConfig.endpointUrl),
        parametersUrl: editingConfig.parametersUrl ? addProtocolToUrl(editingConfig.parametersUrl) : '',
        requestUrl: editingConfig.requestUrl ? addProtocolToUrl(editingConfig.requestUrl) : '',
        lastEdited: Date.now(),
      };

      let updatedConfigs = [...configs];
      
      if (isEditMode) {
        // Update existing config
        const index = configs.findIndex(c => c.id === configToSave.id);
        if (index !== -1) {
          updatedConfigs[index] = configToSave;
        }
      } else {
        // Add new config
        updatedConfigs.push(configToSave);
      }

      // If this config is set as active, deactivate others
      if (configToSave.isActive) {
        updatedConfigs = updatedConfigs.map(c => 
          c.id === configToSave.id ? c : { ...c, isActive: false }
        );
      }

      await ConfigService.saveConfigs(updatedConfigs);
      setConfigs(updatedConfigs);
      setSelectedConfig(configToSave);
      setEditingConfig(configToSave);
      setIsEditMode(true); // After saving, we're now in edit mode
      
      setSuccessMessage(`Document ${isEditMode ? 'updated' : 'added'} successfully!`);
      // Keep the edit form open in Angular style
    } catch (error: any) {
      setErrorMessage('Failed to save document.');
      console.error('Error saving config:', error);
    } finally {
      setSaving(false);
    }
  };

  const setActiveDocument = async (config: Config) => {
    try {
      setSaving(true);
      
      const updatedConfigs = configs.map(c => ({
        ...c,
        isActive: c.id === config.id
      }));
      
      await ConfigService.saveConfigs(updatedConfigs);
      setConfigs(updatedConfigs);
      const updatedConfig = { ...config, isActive: true };
      setSelectedConfig(updatedConfig);
      setEditingConfig(updatedConfig);
      
      setSuccessMessage('Default document updated successfully!');
    } catch (error: any) {
      setErrorMessage('Failed to update default document.');
      console.error('Error updating default:', error);
    } finally {
      setSaving(false);
    }
  };

  const addProtocolToUrl = (url: string): string => {
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const generateEndpoints = () => {
    if (!editingConfig?.endpointUrl) return;

    const baseUrl = editingConfig.endpointUrl.replace(/\/+$|\/v1$/g, '');
    
    if (!editingConfig.parametersUrl) {
      const parametersUrl = baseUrl.endsWith('/parameters') ? baseUrl : `${baseUrl}/v1/parameters`;
      setEditingConfig(prev => prev ? { ...prev, parametersUrl } : null);
    }
    
    if (!editingConfig.requestUrl) {
      const requestUrl = baseUrl.endsWith('/parameters')
        ? baseUrl.substring(0, baseUrl.length - '/parameters'.length)
        : baseUrl;
      setEditingConfig(prev => prev ? { ...prev, requestUrl: `${requestUrl}/v1/workflows/run` } : null);
    }
  };

  const handleIconDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setIconPreview(result);
        setEditingConfig(prev => prev ? { ...prev, icon: result } : null);
      };
      reader.readAsDataURL(file);
    } else {
      toast({ title: 'Only image files are allowed', status: 'error', duration: 2000, isClosable: true });
    }
  };

  const handleIconDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleIconFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setIconPreview(result);
        setEditingConfig(prev => prev ? { ...prev, icon: result } : null);
      };
      reader.readAsDataURL(file);
    } else {
      toast({ title: 'Only image files are allowed', status: 'error', duration: 2000, isClosable: true });
    }
  };

  const moveDocument = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    
    const updatedConfigs = [...configs];
    const [movedConfig] = updatedConfigs.splice(fromIndex, 1);
    updatedConfigs.splice(toIndex, 0, movedConfig);
    
    // Update order property
    updatedConfigs.forEach((config, index) => {
      config.order = index;
    });
    
    setConfigs(updatedConfigs);
    
    // Update "All Documents" collection to match the new global order
    try {
      await CollectionService.reorderDocumentsInCollection('all-documents', fromIndex, toIndex);
      await loadCollections();
    } catch (error) {
      console.error('Failed to update collection order:', error);
    }
    
    // Save to backend
    ConfigService.saveConfigs(updatedConfigs).then(() => {
      setSuccessMessage('Document order updated!');
    }).catch(() => {
      setErrorMessage('Failed to update document order.');
    });
  };

  const copyShareLink = async (config: Config) => {
    if (!config.shareId) return;
    
    const shareUrl = `${window.location.origin}/shared/${config.shareId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Share link copied!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to copy link',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleGenerateShareLink = async (config: Config) => {
    const response = await ConfigService.generateShareLink(config.id, { daysToExpire: 30, permissions: 'execute' });
    // Fetch the updated config from the backend to get the new shareId and isShared
    const updatedConfigs = await ConfigService.getAllConfigs();
    const updatedConfig = updatedConfigs.find(c => c.id === config.id);
    if (updatedConfig) {
      setEditingConfig(updatedConfig);
      setConfigs(prev => prev.map(c => c.id === config.id ? updatedConfig : c));
    }
    toast({ title: 'Share link generated', status: 'success', duration: 2000, isClosable: true });
  };

  const handleDeleteShareLink = async (config: Config) => {
    await ConfigService.deleteShareLink(config.id);
    const updatedConfig = { ...config, isShared: false, shareId: undefined };
    setEditingConfig(updatedConfig);
    setConfigs(prev => prev.map(c => c.id === config.id ? updatedConfig : c));
    toast({ title: 'Share link deleted', status: 'info', duration: 2000, isClosable: true });
  };

  const handleRegenerateShareLink = async (config: Config) => {
    await ConfigService.deleteShareLink(config.id);
    const response = await ConfigService.generateShareLink(config.id, { daysToExpire: 30, permissions: 'execute' });
    const updatedConfig = { ...config, isShared: true, shareId: response.shareId };
    setEditingConfig(updatedConfig);
    setConfigs(prev => prev.map(c => c.id === config.id ? updatedConfig : c));
    toast({ title: 'Share link regenerated', status: 'success', duration: 2000, isClosable: true });
  };

  const loadGitlabToken = async () => {
    try {
      const result = await GitLabService.checkToken();
      setGitlabTokenSaved(result.hasToken);
    } catch (error) {
      console.error('Error checking GitLab token:', error);
    }
  };

  const loadDefaultGitlabUrl = async () => {
    try {
      const result = await GitLabService.getDefaultRepository();
      if (result.url) {
        setDefaultGitlabUrl(result.url);
        setDefaultGitlabUrlSaved(true);
      } else {
        setDefaultGitlabUrlSaved(false);
      }
    } catch (error) {
      console.error('Error loading default GitLab URL:', error);
      setDefaultGitlabUrlSaved(false);
    }
  };

  const saveGitlabToken = async () => {
    try {
      setSaving(true);
      await GitLabService.setToken(gitlabToken);
      setGitlabTokenSaved(true);
      setGitlabToken('');
      setEditingGitlabToken(false);
      toast({ 
        title: 'GitLab token saved successfully', 
        status: 'success', 
        duration: 3000, 
        isClosable: true 
      });
    } catch (error) {
      console.error('Error saving GitLab token:', error);
      let errorMessage = 'Failed to save GitLab token';
      
      if (error instanceof Error) {
        if (error.message.includes('authentication')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({ 
        title: 'Failed to save GitLab token', 
        description: errorMessage,
        status: 'error', 
        duration: 5000, 
        isClosable: true 
      });
    } finally {
      setSaving(false);
    }
  };

  const startEditingGitlabToken = () => {
    setEditingGitlabToken(true);
    setGitlabTokenSaved(false);
    setGitlabToken('');
  };

  const cancelEditingGitlabToken = () => {
    setEditingGitlabToken(false);
    setGitlabToken('');
    // Re-check if token exists
    loadGitlabToken();
  };

  const saveDefaultGitlabUrl = async () => {
    try {
      setSaving(true);
      await GitLabService.setDefaultRepository(defaultGitlabUrl);
      setDefaultGitlabUrlSaved(true);
      setEditingDefaultGitlabUrl(false);
      toast({ 
        title: 'Default repository saved successfully', 
        status: 'success', 
        duration: 3000, 
        isClosable: true 
      });
    } catch (error) {
      console.error('Error saving default GitLab URL:', error);
      let errorMessage = 'Failed to save default repository';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: 'Failed to save default repository', 
        description: errorMessage,
        status: 'error', 
        duration: 5000, 
        isClosable: true 
      });
    } finally {
      setSaving(false);
    }
  };

  const startEditingDefaultGitlabUrl = () => {
    setEditingDefaultGitlabUrl(true);
    // Keep current value for editing
  };

  const cancelEditingDefaultGitlabUrl = () => {
    setEditingDefaultGitlabUrl(false);
    // Reset to saved value
    loadDefaultGitlabUrl();
  };

  // User management handlers
  const loadUsers = async () => {
    setUserLoading(true);
    setUserError('');
    try {
      const users = await UserService.getUsers();
      setUsers(users);
    } catch (e: any) {
      setUserError(e.message || 'Failed to load users');
    } finally {
      setUserLoading(false);
    }
  };

  const handleUserFormOpen = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({ username: user.username, email: user.email || '', password: '', is_admin: user.is_admin || false });
    } else {
      setEditingUser(null);
      setUserForm({ username: '', email: '', password: '', is_admin: false });
    }
    setUserFormError('');
    setShowUserForm(true);
  };

  const handleUserFormClose = () => {
    setShowUserForm(false);
    setEditingUser(null);
    setUserForm({ username: '', email: '', password: '', is_admin: false });
    setUserFormError('');
  };

  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserForm({ ...userForm, [e.target.name]: e.target.value });
  };

  const handleUserFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserFormSaving(true);
    setUserFormError('');
    try {
      if (!userForm.username.trim() || (!editingUser && !userForm.password.trim())) {
        setUserFormError('Username and password are required');
        setUserFormSaving(false);
        return;
      }
      if (editingUser) {
        await UserService.updateUser(editingUser.id, {
          username: userForm.username,
          email: userForm.email,
          password: userForm.password || undefined,
          is_admin: userForm.is_admin,
        });
      } else {
        await UserService.addUser({
          username: userForm.username,
          email: userForm.email,
          password: userForm.password,
          is_admin: userForm.is_admin,
        });
      }
      await loadUsers();
      await loadUsersWithAccess(); // Refresh access data
      handleUserFormClose();
      toast({ title: editingUser ? 'User updated' : 'User added', status: 'success', duration: 2000, isClosable: true });
    } catch (e: any) {
      setUserFormError(e.message || 'Failed to save user');
    } finally {
      setUserFormSaving(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Delete user '${user.username}'?`)) return;
    try {
      await UserService.deleteUser(user.id);
      await loadUsers();
      await loadUsersWithAccess(); // Refresh access data
      toast({ title: 'User deleted', status: 'info', duration: 2000, isClosable: true });
    } catch (e: any) {
      toast({ title: 'Failed to delete user', description: e.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  // User-Collection access management functions
  const loadUsersWithAccess = async () => {
    setAccessLoading(true);
    try {
      const usersWithAccessData = await UserService.getUsersWithCollectionAccess();
      setUsersWithAccess(usersWithAccessData);
      
      // Update selectedUserForAccess if it exists in the new data
      if (selectedUserForAccess) {
        const updatedSelectedUser = usersWithAccessData.find(u => u.id === selectedUserForAccess.id);
        if (updatedSelectedUser) {
          setSelectedUserForAccess(updatedSelectedUser);
        }
      }
    } catch (e: any) {
      console.error('Failed to load users with access:', e);
      toast({ 
        title: 'Failed to load user access data', 
        description: e.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    } finally {
      setAccessLoading(false);
    }
  };

  const toggleUserCollectionAccess = async (userId: string, collectionId: string, hasAccess: boolean) => {
    const accessKey = `${userId}-${collectionId}`;
    setAccessUpdating(prev => ({ ...prev, [accessKey]: true }));
    
    try {
      if (hasAccess) {
        await UserService.revokeCollectionAccess(userId, collectionId);
      } else {
        await UserService.grantCollectionAccess(userId, collectionId, 'read');
      }
      
      await loadUsersWithAccess(); // Refresh data
      
      const action = hasAccess ? 'revoked' : 'granted';
      toast({ 
        title: `Access ${action}`, 
        status: 'success', 
        duration: 2000, 
        isClosable: true 
      });
    } catch (e: any) {
      toast({ 
        title: 'Failed to update access', 
        description: e.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    } finally {
      setAccessUpdating(prev => ({ ...prev, [accessKey]: false }));
    }
  };

  // Collections management functions
  const loadCollections = async () => {
    try {
      const loadedCollections = await CollectionService.getCollections();
      setCollections(loadedCollections);
      
      // Attempt migration if collections were loaded successfully
      await CollectionService.migrateLocalStorageCollections();
    } catch (error: any) {
      console.error('Error loading collections:', error);
      
      if (error.message.includes('Authentication required')) {
        toast({ 
          title: 'Authentication Required', 
          description: 'Please refresh the page and log in again to manage collections.',
          status: 'warning', 
          duration: 5000, 
          isClosable: true 
        });
        return;
      }
      
      toast({ 
        title: 'Failed to load collections', 
        description: error.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    }
  };

  const handleCollectionFormOpen = (collection?: Collection) => {
    if (collection) {
      setEditingCollection(collection);
      setCollectionForm({
        name: collection.name,
        description: collection.description || '',
        isDefault: collection.isDefault,
        documentIds: [...collection.documentIds]
      });
    } else {
      setEditingCollection(null);
      setCollectionForm({ name: '', description: '', isDefault: false, documentIds: [] });
    }
    setCollectionFormError('');
    setShowCollectionForm(true);
  };

  const handleCollectionFormClose = () => {
    setShowCollectionForm(false);
    setEditingCollection(null);
    setCollectionForm({ name: '', description: '', isDefault: false, documentIds: [] });
    setCollectionFormError('');
  };

  const handleCollectionFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCollectionFormSaving(true);
    setCollectionFormError('');
    try {
      if (!collectionForm.name.trim()) {
        setCollectionFormError('Collection name is required');
        setCollectionFormSaving(false);
        return;
      }

      if (editingCollection) {
        await CollectionService.updateCollection(editingCollection.id, collectionForm);
      } else {
        await CollectionService.createCollection(collectionForm);
      }
      
      await loadCollections();
      await loadUsersWithAccess(); // Refresh access data when collections change
      handleCollectionFormClose();
      toast({ title: editingCollection ? 'Collection updated' : 'Collection created', status: 'success', duration: 2000, isClosable: true });
    } catch (e: any) {
      setCollectionFormError(e.message || 'Failed to save collection');
    } finally {
      setCollectionFormSaving(false);
    }
  };

  const handleDeleteCollection = async (collection: Collection) => {
    if (!window.confirm(`Delete collection '${collection.name}'?`)) return;
    try {
      await CollectionService.deleteCollection(collection.id);
      await loadCollections();
      await loadUsersWithAccess(); // Refresh access data when collections are deleted
      toast({ title: 'Collection deleted', status: 'info', duration: 2000, isClosable: true });
    } catch (e: any) {
      toast({ title: 'Failed to delete collection', description: e.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleToggleDocumentInCollection = async (collectionId: string, documentId: string, isInCollection: boolean) => {
    try {
      if (isInCollection) {
        await CollectionService.removeDocumentFromCollection(collectionId, documentId);
      } else {
        await CollectionService.addDocumentToCollection(collectionId, documentId);
      }
      await loadCollections();
    } catch (e: any) {
      toast({ title: 'Failed to update collection', description: e.message, status: 'error', duration: 2000, isClosable: true });
    }
  };

  const handleCloneCollection = async (collection: Collection) => {
    // Generate a unique name for the cloned collection
    let cloneName = `${collection.name} Copy`;
    let counter = 1;
    
    // Keep trying different names until we find one that doesn't exist
    while (collections.some(c => c.name.toLowerCase() === cloneName.toLowerCase())) {
      counter++;
      cloneName = `${collection.name} Copy ${counter}`;
    }
    
    const cloneDescription = `Copy of ${collection.name}`;
    
    try {
      await CollectionService.cloneCollection(collection.id, cloneName, cloneDescription);
      await loadCollections();
      toast({ 
        title: 'Collection cloned successfully', 
        description: `Created "${cloneName}"`,
        status: 'success', 
        duration: 3000, 
        isClosable: true 
      });
    } catch (e: any) {
      toast({ 
        title: 'Failed to clone collection', 
        description: e.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    }
  };

  // Collection sharing methods
  const handleGenerateCollectionShareLink = async (collection: Collection) => {
    try {
      const result = await CollectionService.generateShareLink(collection.id, { expirationDays: 30, permissions: 'view' });
      await loadCollections();
      
      // Copy the share link to clipboard
      const shareUrl = `${window.location.origin}/shared/${result.shareUrl}`;
      await navigator.clipboard.writeText(shareUrl);
      
      toast({ 
        title: 'Collection share link generated', 
        description: 'Link copied to clipboard',
        status: 'success', 
        duration: 3000, 
        isClosable: true 
      });
    } catch (e: any) {
      toast({ 
        title: 'Failed to generate share link', 
        description: e.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    }
  };

  const handleRemoveCollectionShareLink = async (collection: Collection) => {
    try {
      await CollectionService.removeShareLink(collection.id);
      await loadCollections();
      toast({ 
        title: 'Collection share link removed', 
        status: 'info', 
        duration: 2000, 
        isClosable: true 
      });
    } catch (e: any) {
      toast({ 
        title: 'Failed to remove share link', 
        description: e.message, 
        status: 'error', 
        duration: 3000, 
        isClosable: true 
      });
    }
  };

  const copyCollectionShareLink = async (collection: Collection) => {
    if (!collection.shareId) return;
    
    const shareUrl = `${window.location.origin}/shared/${collection.shareId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Collection share link copied!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to copy link',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box w="100vw" h="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={8}>
          <Text>Loading configurations...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box w="100vw" h="100%" minH="calc(100vh - 80px)" px={0} py={0}>
      <VStack spacing={8} align="stretch" w="100%" h="100%" px={0} py={0}>
        {/* Settings Header */}
        <HStack justify="space-between" align="center" px={8} pt={8}>
          <HStack spacing={4}>
            <SettingsIcon boxSize={6} color={textColor} />
            <Heading size="lg" color={textColor}>
              Settings
            </Heading>
          </HStack>
          <HStack spacing={4}>
            <Button
              leftIcon={<AddIcon />}
              colorScheme={themeConfig.colorScheme}
              onClick={addNewDocument}
              size="md"
            >
              Add New Document
            </Button>
          </HStack>
        </HStack>

        {/* Tabs */}
        <Tabs isFitted variant="line" colorScheme={themeConfig.colorScheme}>
          <TabList px={8} borderBottom="1px" borderColor={borderColor}>
            <Tab 
              fontWeight="semibold" 
              fontSize="md"
              color={mutedColor}
              _selected={{ 
                color: textColor,
                borderColor: `${themeConfig.colorScheme}.500`
              }}
              _hover={{
                color: textColor
              }}
            >
              Document Configurations
            </Tab>
            <Tab 
              fontWeight="semibold" 
              fontSize="md"
              color={mutedColor}
              _selected={{ 
                color: textColor,
                borderColor: `${themeConfig.colorScheme}.500`
              }}
              _hover={{
                color: textColor
              }}
            >
              GitLab Integration
            </Tab>
            <Tab fontWeight="semibold" fontSize="md" color={mutedColor} _selected={{ color: textColor, borderColor: `${themeConfig.colorScheme}.500` }} _hover={{ color: textColor }}>User Management</Tab>
            <Tab fontWeight="semibold" fontSize="md" color={mutedColor} _selected={{ color: textColor, borderColor: `${themeConfig.colorScheme}.500` }} _hover={{ color: textColor }}>Collections</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              {/* Document Configurations Tab Content */}

        {/* Status Messages */}
        {successMessage && (
          <Alert status="success" borderRadius="md" mx={8}>
            <AlertIcon />
            <AlertTitle>Success!</AlertTitle>
            <AlertDescription>{successMessage}</AlertDescription>
            <CloseButton position="absolute" right="8px" top="8px" onClick={() => setSuccessMessage('')} />
          </Alert>
        )}

        {errorMessage && (
          <Alert status="error" borderRadius="md" mx={8}>
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
            <CloseButton position="absolute" right="8px" top="8px" onClick={() => setErrorMessage('')} />
          </Alert>
        )}

        {/* Settings Content - Two-Panel Layout */}
        <Grid templateColumns="420px 1fr" gap={0} h="calc(100vh - 220px)" w="100%" px={8}>
          {/* Left Panel: Configuration List */}
          <Card bg={cardBg} borderColor={borderColor} borderRightRadius={0}>
            <CardHeader>
              <Heading size="md" color={textColor}>
                Configurations
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                Select a configuration to edit
              </Text>
            </CardHeader>
            <CardBody p={0} overflowY="auto" maxHeight="60vh">
              <VStack spacing={0} align="stretch">
                {configs.map((config, index) => (
                  <Box
                    key={config.id}
                    bg={selectedConfig?.id === config.id ? selectedBg : cardBg}
                    borderColor={config.isActive ? `${themeConfig.colorScheme}.300` : borderColor}
                    borderWidth={config.isActive ? 2 : 1}
                    borderLeft={selectedConfig?.id === config.id ? '4px solid' : 'none'}
                    borderLeftColor={selectedConfig?.id === config.id ? `${themeConfig.colorScheme}.500` : 'transparent'}
                    cursor="pointer"
                    _hover={{ bg: hoverBg }}
                    onClick={(e) => {
                      if (draggedIndex === null) {
                        editDocument(config);
                      }
                    }}
                    p={4}
                    borderBottom="1px"
                    borderBottomColor={borderColor}
                    className={selectedConfig?.id === config.id ? 'selected' : ''}
                    position="relative"
                    draggable
                    onDragStart={(e) => {
                      setDraggedIndex(index);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggedIndex !== null && draggedIndex !== index) {
                        moveDocument(draggedIndex, index);
                      }
                      setDraggedIndex(null);
                    }}
                    onDragEnd={() => setDraggedIndex(null)}
                    opacity={draggedIndex === index ? 0.5 : 1}
                  >
                    <HStack spacing={3}>
                      <IconButton
                        aria-label="Drag to reorder"
                        icon={<DragHandleIcon />}
                        size="sm"
                        variant="ghost"
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.currentTarget.closest('[draggable="true"]')?.setAttribute('draggable', 'true');
                        }}
                      />
                      
                      {config.icon ? (
                        <Image src={config.icon} alt="Configuration icon" boxSize={8} />
                      ) : (
                        <Box w={8} h={8} bg={mutedColor} borderRadius="md" />
                      )}
                      
                      <VStack align="start" spacing={0} flex={1}>
                        <HStack>
                          <Text fontWeight="semibold" color={config.isEnabled ? textColor : mutedColor}>
                            {config.name || 'Untitled Configuration'}
                          </Text>
                          {config.isActive && (
                            <Badge colorScheme={themeConfig.colorScheme} variant="solid" size="sm">
                              Default
                            </Badge>
                          )}
                          {!config.isEnabled && (
                            <Badge colorScheme="red" variant="outline" size="sm">
                              Disabled
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color={mutedColor} noOfLines={1}>
                          {config.description || 'No description'}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
                
                {configs.length === 0 && (
                  <Text color={mutedColor} textAlign="center" py={8}>
                    No configurations found. Click "Add New Document" to create one.
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Right Panel: Configuration Editor */}
          <Card bg={cardBg} borderColor={borderColor} borderLeftRadius={0}>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md" color={textColor}>
                  {editingConfig ? (isEditMode ? `Edit ${editingConfig.name || 'Configuration'}` : 'New Configuration') : 'Select a Configuration'}
                </Heading>
                {editingConfig && (
                  <HStack spacing={6}>
                    <HStack>
                      <Switch
                        isChecked={editingConfig.isEnabled}
                        onChange={async (e) => {
                          const newEnabled = e.target.checked;
                          const updatedConfig = { ...editingConfig, isEnabled: newEnabled };
                          setEditingConfig(updatedConfig);
                          
                          // Auto-save the enabled state
                          try {
                            setSaving(true);
                            const updatedConfigs = configs.map(c => 
                              c.id === editingConfig.id ? updatedConfig : c
                            );
                            await ConfigService.saveConfigs(updatedConfigs);
                            setConfigs(updatedConfigs);
                            setSelectedConfig(updatedConfig);
                            
                            toast({
                              title: `Document ${newEnabled ? 'enabled' : 'disabled'}`,
                              status: 'success',
                              duration: 2000,
                              isClosable: true,
                            });
                          } catch (error: any) {
                            // Revert the change on error
                            setEditingConfig(editingConfig);
                            toast({
                              title: 'Failed to update document',
                              description: error.message,
                              status: 'error',
                              duration: 3000,
                              isClosable: true,
                            });
                          } finally {
                            setSaving(false);
                          }
                        }}
                        colorScheme="green"
                        size="lg"
                      />
                      <Text fontSize="sm" color={editingConfig.isEnabled ? 'green.500' : 'gray.500'}>
                        {editingConfig.isEnabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    </HStack>
                    <HStack>
                      <Switch
                        isChecked={editingConfig.isActive}
                        onChange={(e) => setEditingConfig({ ...editingConfig, isActive: e.target.checked })}
                        colorScheme={themeConfig.colorScheme}
                        size="lg"
                      />
                      <Text fontSize="sm" color={mutedColor}>
                        Default
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </HStack>
            </CardHeader>
            <CardBody overflowY="auto">
              {editingConfig ? (
                <VStack spacing={6} align="stretch">
                  {/* Configuration Form Fields */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!errors.name}>
                      <FormLabel color={textColor}>Name*</FormLabel>
                      <Input
                        value={editingConfig.name}
                        onChange={(e) => setEditingConfig({ ...editingConfig, name: e.target.value })}
                        placeholder="Enter configuration name"
                      />
                      <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>Description</FormLabel>
                      <Input
                        value={editingConfig.description}
                        onChange={(e) => setEditingConfig({ ...editingConfig, description: e.target.value })}
                        placeholder="Enter configuration description"
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.apiKey}>
                      <FormLabel color={textColor}>API Key*</FormLabel>
                      <Input
                        value={editingConfig.apiKey}
                        onChange={(e) => setEditingConfig({ ...editingConfig, apiKey: e.target.value })}
                        placeholder="Enter API key"
                        type="password"
                      />
                      <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>Cue Text</FormLabel>
                      <Input
                        value={editingConfig.cueText}
                        onChange={(e) => setEditingConfig({ ...editingConfig, cueText: e.target.value })}
                        placeholder="Enter cue text"
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.endpointUrl}>
                      <FormLabel color={textColor}>Endpoint URL*</FormLabel>
                      <Input
                        value={editingConfig.endpointUrl}
                        onChange={(e) => {
                          setEditingConfig({ ...editingConfig, endpointUrl: e.target.value });
                          generateEndpoints();
                        }}
                        placeholder="Enter endpoint URL"
                      />
                      <FormErrorMessage>{errors.endpointUrl}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>Parameters URL</FormLabel>
                      <Input
                        value={editingConfig.parametersUrl}
                        onChange={(e) => setEditingConfig({ ...editingConfig, parametersUrl: e.target.value })}
                        placeholder="Auto-generated from endpoint URL"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>Request URL</FormLabel>
                      <Input
                        value={editingConfig.requestUrl}
                        onChange={(e) => setEditingConfig({ ...editingConfig, requestUrl: e.target.value })}
                        placeholder="Auto-generated from endpoint URL"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>GitLab URL</FormLabel>
                      <Input
                        value={editingConfig.gitlabUrl || ''}
                        onChange={(e) => setEditingConfig({ ...editingConfig, gitlabUrl: e.target.value })}
                        placeholder={defaultGitlabUrlSaved ? `Default: ${defaultGitlabUrl}` : "Leave empty to use default repository"}
                        onBlur={async (e) => {
                          const url = e.target.value.trim();
                          if (url && !url.startsWith('http')) {
                            // Auto-add https:// if protocol is missing
                            setEditingConfig({ ...editingConfig, gitlabUrl: `https://${url}` });
                          }
                        }}
                      />
                      <Text fontSize="xs" color={mutedColor} mt={1}>
                        {defaultGitlabUrlSaved 
                          ? "Leave empty to use default repository, or specify a different repository for this document. Supports both project URLs and file browser URLs." 
                          : "Format: https://your-gitlab-domain.com/namespace/project-name or file browser: https://gitlab.com/namespace/project/-/tree/main/folder"}
                      </Text>
                    </FormControl>

                    <FormControl gridColumn={{ base: 'span 1', md: 'span 1' }}>
                      <FormLabel color={textColor}>Share Link</FormLabel>
                      <VStack align="stretch" spacing={2}>
                        {editingConfig.isShared && editingConfig.shareId ? (
                          <HStack>
                            <Input
                              value={`${window.location.origin}/shared/${editingConfig.shareId}`}
                              isReadOnly
                              size="sm"
                              flex={1}
                              onClick={e => (e.target as HTMLInputElement).select()}
                            />
                            <Tooltip label="Copy link">
                              <IconButton
                                aria-label="Copy link"
                                icon={<CopyIcon />}
                                size="sm"
                                onClick={() => copyShareLink(editingConfig)}
                              />
                            </Tooltip>
                            <Tooltip label="Regenerate link">
                              <IconButton
                                aria-label="Regenerate link"
                                icon={<RepeatIcon />}
                                size="sm"
                                onClick={() => handleRegenerateShareLink(editingConfig)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete link">
                              <IconButton
                                aria-label="Delete link"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteShareLink(editingConfig)}
                              />
                            </Tooltip>
                          </HStack>
                        ) : (
                          <Button
                            leftIcon={<ExternalLinkIcon />}
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateShareLink(editingConfig)}
                          >
                            Generate Share Link
                          </Button>
                        )}
                      </VStack>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={textColor}>Icon</FormLabel>
                      <VStack spacing={3}>
                        <Box
                          border="2px dashed"
                          borderColor={iconPreview ? `${themeConfig.colorScheme}.300` : borderColor}
                          borderRadius="md"
                          p={4}
                          textAlign="center"
                          bg={iconPreview ? `${themeConfig.colorScheme}.50` : 'transparent'}
                          cursor="pointer"
                          onDrop={handleIconDrop}
                          onDragOver={handleIconDragOver}
                          onClick={() => fileInputRef.current?.click()}
                          transition="all 0.2s"
                        >
                          {iconPreview ? (
                            <Image src={iconPreview} alt="Configuration icon preview" boxSize={16} mx="auto" />
                          ) : (
                            <Text color={mutedColor} fontSize="sm">Drag & drop an image here, or click to browse</Text>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleIconFileChange}
                            style={{ display: 'none' }}
                          />
                        </Box>
                        {iconPreview && (
                          <Button
                            leftIcon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => {
                              setIconPreview(null);
                              setEditingConfig(prev => prev ? { ...prev, icon: '' } : null);
                            }}
                          >
                            Remove Icon
                          </Button>
                        )}
                      </VStack>
                    </FormControl>
                  </SimpleGrid>
                  
                  <Divider />
                  
                  {/* Configuration Actions */}
                  <HStack justify="space-between">
                    <Button
                      colorScheme={themeConfig.colorScheme}
                      onClick={saveDocument}
                      isLoading={saving}
                      loadingText="Saving..."
                    >
                      Save Configuration
                    </Button>
                    {isEditMode && (
                      <Button
                        leftIcon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        onClick={() => deleteDocument(editingConfig)}
                      >
                        Delete Configuration
                      </Button>
                    )}
                  </HStack>
                </VStack>
              ) : (
                <Text color={mutedColor} textAlign="center" py={8}>
                  Select a configuration from the list to edit its details.
                </Text>
              )}
            </CardBody>
          </Card>
        </Grid>

            </TabPanel>
            <TabPanel px={8}>
              {/* GitLab Integration Tab Content */}
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md" color={textColor}>
                    GitLab Integration
                  </Heading>
                  <Text fontSize="sm" color={mutedColor}>
                    Configure GitLab API access for file browsing and uploading
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <FormControl>
                      <FormLabel color={textColor}>Default GitLab Repository</FormLabel>
                      {defaultGitlabUrlSaved && !editingDefaultGitlabUrl ? (
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Text fontSize="sm" color="green.500" flex={1}>
                              ✓ Default repository: {defaultGitlabUrl}
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme={themeConfig.colorScheme}
                              onClick={startEditingDefaultGitlabUrl}
                            >
                              Update Repository
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Input
                              value={defaultGitlabUrl}
                              onChange={(e) => setDefaultGitlabUrl(e.target.value)}
                              placeholder="https://your-gitlab-domain.com/namespace/project-name or with path"
                              onBlur={async (e) => {
                                const url = e.target.value.trim();
                                if (url && !url.startsWith('http')) {
                                  // Auto-add https:// if protocol is missing
                                  setDefaultGitlabUrl(`https://${url}`);
                                }
                              }}
                            />
                            <Button
                              colorScheme={themeConfig.colorScheme}
                              onClick={saveDefaultGitlabUrl}
                              isLoading={saving}
                              isDisabled={!defaultGitlabUrl.trim()}
                            >
                              Save
                            </Button>
                            {editingDefaultGitlabUrl && (
                              <Button
                                variant="ghost"
                                onClick={cancelEditingDefaultGitlabUrl}
                              >
                                Cancel
                              </Button>
                            )}
                          </HStack>
                        </VStack>
                      )}
                      <Text fontSize="xs" color={mutedColor} mt={2}>
                        This repository will be used by default for all documents unless overridden. Supports both project URLs and file browser URLs.
                      </Text>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel color={textColor}>GitLab Personal Access Token</FormLabel>
                      {gitlabTokenSaved && !editingGitlabToken ? (
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Text fontSize="sm" color="green.500" flex={1}>
                              ✓ GitLab token configured successfully
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme={themeConfig.colorScheme}
                              onClick={startEditingGitlabToken}
                            >
                              Update Token
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Input
                              type="password"
                              value={gitlabToken}
                              onChange={(e) => setGitlabToken(e.target.value)}
                              placeholder="Enter your GitLab Personal Access Token"
                            />
                            <Button
                              colorScheme={themeConfig.colorScheme}
                              onClick={saveGitlabToken}
                              isLoading={saving}
                              isDisabled={!gitlabToken}
                            >
                              Save
                            </Button>
                            {editingGitlabToken && (
                              <Button
                                variant="ghost"
                                onClick={cancelEditingGitlabToken}
                              >
                                Cancel
                              </Button>
                            )}
                          </HStack>
                        </VStack>
                      )}
                    </FormControl>
                    
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color={textColor} mb={2}>
                        How to get a GitLab Personal Access Token:
                      </Text>
                      <VStack align="start" spacing={2} fontSize="sm" color={mutedColor}>
                        <Text>1. Go to GitLab.com → User Settings → Access Tokens</Text>
                        <Text>2. Create a new token with the following scopes:</Text>
                        <Text ml={4}>• read_api</Text>
                        <Text ml={4}>• read_repository</Text>
                        <Text ml={4}>• write_repository</Text>
                        <Text>3. Copy the token and paste it above</Text>
                      </VStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
            <TabPanel px={8}>
              {/* Consolidated User Management Tab Content */}
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color={textColor}>User & Collection Access Management</Heading>
                      <Text fontSize="sm" color={mutedColor}>
                        Manage users and control their collection access. Admin users have full access to all collections.
                      </Text>
                    </VStack>
                    <Button colorScheme={themeConfig.colorScheme} onClick={() => handleUserFormOpen()}>Add User</Button>
                  </HStack>
                </CardHeader>
                <CardBody p={0}>
                  {(userError || accessLoading) && (
                    <Box p={6}>
                      {userError && <Alert status="error" mb={4}><AlertIcon />{userError}</Alert>}
                      {accessLoading && <Text>Loading access data...</Text>}
                    </Box>
                  )}
                  {!accessLoading && usersWithAccess.length === 0 ? (
                    <Box p={6}>
                      <Text color={mutedColor}>No users found.</Text>
                    </Box>
                  ) : !accessLoading && (
                    <Grid templateColumns="400px 1fr" gap={0} h="calc(100vh - 300px)" maxH="700px">
                      {/* Left Panel: Users List */}
                      <Card bg={cardBg} borderColor={borderColor} borderRightRadius={0} h="full">
                        <CardHeader pb={2} flexShrink={0}>
                          <HStack justify="space-between" align="start">
                            <VStack align="start" spacing={0}>
                              <Heading size="sm" color={textColor}>Users</Heading>
                              <Text fontSize="xs" color={mutedColor}>Select a user to manage access</Text>
                            </VStack>
                          </HStack>
                        </CardHeader>
                        <CardBody p={0} overflowY="auto" h="full">
                          <VStack spacing={0} align="stretch">
                            {usersWithAccess.map(user => (
                              <Box
                                key={user.id}
                                bg={selectedUserForAccess?.id === user.id ? selectedBg : cardBg}
                                borderLeft={selectedUserForAccess?.id === user.id ? '4px solid' : 'none'}
                                borderLeftColor={selectedUserForAccess?.id === user.id ? `${themeConfig.colorScheme}.500` : 'transparent'}
                                cursor="pointer"
                                _hover={{ bg: hoverBg }}
                                onClick={() => setSelectedUserForAccess(user)}
                                p={4}
                                borderBottom="1px"
                                borderBottomColor={borderColor}
                                position="relative"
                              >
                                <HStack justify="space-between" align="start">
                                  <VStack align="start" spacing={1} flex={1} pr={2}>
                                    <HStack>
                                      <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                                        {user.username}
                                      </Text>
                                      {user.is_admin && (
                                        <Badge colorScheme="purple" variant="solid" size="sm">
                                          Admin
                                        </Badge>
                                      )}
                                    </HStack>
                                    <Text fontSize="xs" color={mutedColor}>
                                      {user.email || 'No email'}
                                    </Text>
                                    <Text fontSize="xs" color={mutedColor}>
                                      {user.is_admin 
                                        ? 'Full access (Admin)' 
                                        : `${Array.isArray(user.collection_access) ? user.collection_access.length : 0} collections accessible`
                                      }
                                    </Text>
                                  </VStack>
                                  <VStack spacing={2} position="absolute" right={2} top={2}>
                                    <Tooltip label="Edit User">
                                      <IconButton 
                                        aria-label="Edit user" 
                                        icon={<EditIcon />} 
                                        size="sm" 
                                        variant="solid"
                                        colorScheme={themeConfig.colorScheme}
                                        boxSize={8}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUserFormOpen(user);
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip label="Delete User">
                                      <IconButton 
                                        aria-label="Delete user" 
                                        icon={<DeleteIcon />} 
                                        size="sm" 
                                        variant="solid"
                                        colorScheme="red"
                                        boxSize={8}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteUser(user);
                                        }}
                                      />
                                    </Tooltip>
                                  </VStack>
                                </HStack>
                              </Box>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>

                      {/* Right Panel: Collection Access Manager */}
                      <Card bg={cardBg} borderColor={borderColor} borderLeftRadius={0} h="full">
                        <CardHeader pb={2} flexShrink={0}>
                          <HStack justify="space-between">
                            <Heading size="sm" color={textColor}>
                              {selectedUserForAccess 
                                ? `${selectedUserForAccess.is_admin ? 'Admin User' : 'Collection Access'} - ${selectedUserForAccess.username}` 
                                : 'Select a User'
                              }
                            </Heading>
                          </HStack>
                          {selectedUserForAccess && (
                            <Text fontSize="xs" color={mutedColor}>
                              {selectedUserForAccess.is_admin 
                                ? 'Admin users have full access to all collections and settings'
                                : 'Toggle collections that this user can access'
                              }
                            </Text>
                          )}
                        </CardHeader>
                        <CardBody overflowY="auto" h="full">
                          {selectedUserForAccess ? (
                            <VStack spacing={4} align="stretch">
                              {selectedUserForAccess.is_admin ? (
                                <Alert status="success" borderRadius="md">
                                  <AlertIcon />
                                  <Box>
                                    <Text fontSize="sm" fontWeight="bold">
                                      Administrator Access
                                    </Text>
                                    <Text fontSize="sm">
                                      This user has admin privileges and can access all collections, documents, and settings.
                                    </Text>
                                  </Box>
                                </Alert>
                              ) : (
                                <>
                                  <Alert status="info" borderRadius="md" size="sm">
                                    <AlertIcon boxSize={4} />
                                    <Box>
                                      <Text fontSize="sm">
                                        <strong>Note:</strong> Shared links bypass these restrictions - users can access any collection via shared links.
                                      </Text>
                                    </Box>
                                  </Alert>

                                  {collections.length === 0 ? (
                                    <Text color={mutedColor} textAlign="center" py={8}>
                                      No collections found. Create collections in the Collections tab.
                                    </Text>
                                  ) : (
                                    <VStack spacing={3} align="stretch">
                                      {collections.map(collection => {
                                        const userCollectionAccess = Array.isArray(selectedUserForAccess.collection_access) ? selectedUserForAccess.collection_access : [];
                                        const hasAccess = userCollectionAccess.some(access => access.collection_id === collection.id);
                                        const accessKey = `${selectedUserForAccess.id}-${collection.id}`;
                                        const isUpdating = accessUpdating[accessKey] || false;
                                        
                                        return (
                                          <Card 
                                            key={collection.id} 
                                            variant="outline" 
                                            borderColor={hasAccess ? `${themeConfig.colorScheme}.300` : borderColor}
                                            bg={hasAccess ? selectedBg : 'transparent'}
                                          >
                                            <CardBody p={4}>
                                              <HStack justify="space-between" align="start">
                                                <VStack align="start" spacing={1} flex={1}>
                                                  <HStack>
                                                    <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                                                      {collection.name}
                                                    </Text>
                                                    {collection.isDefault && (
                                                      <Badge colorScheme={themeConfig.colorScheme} variant="solid" size="sm">
                                                        Default
                                                      </Badge>
                                                    )}
                                                    {collection.isShared && (
                                                      <Badge colorScheme="green" variant="outline" size="sm">
                                                        Shared
                                                      </Badge>
                                                    )}
                                                  </HStack>
                                                  <Text fontSize="xs" color={mutedColor}>
                                                    {collection.description || 'No description'}
                                                  </Text>
                                                  <Text fontSize="xs" color={mutedColor}>
                                                    {collection.documentIds.length} document{collection.documentIds.length !== 1 ? 's' : ''}
                                                  </Text>
                                                </VStack>
                                                <VStack align="end" spacing={1}>
                                                  <Switch
                                                    isChecked={hasAccess}
                                                    onChange={() => toggleUserCollectionAccess(selectedUserForAccess.id, collection.id, hasAccess)}
                                                    colorScheme={themeConfig.colorScheme}
                                                    size="lg"
                                                    isDisabled={isUpdating}
                                                  />
                                                  <Text fontSize="xs" color={hasAccess ? 'green.500' : 'gray.500'}>
                                                    {hasAccess ? 'Accessible' : 'Restricted'}
                                                  </Text>
                                                </VStack>
                                              </HStack>
                                            </CardBody>
                                          </Card>
                                        );
                                      })}
                                    </VStack>
                                  )}
                                </>
                              )}
                            </VStack>
                          ) : (
                            <Text color={mutedColor} textAlign="center" py={8}>
                              Select a user from the left panel to manage their collection access.
                            </Text>
                          )}
                        </CardBody>
                      </Card>
                    </Grid>
                  )}
                </CardBody>
              </Card>

              {/* User Add/Edit Modal */}
              {showUserForm && (
                <Box position="fixed" top={0} left={0} w="100vw" h="100vh" bg="blackAlpha.600" zIndex={1000} display="flex" alignItems="center" justifyContent="center">
                  <Box bg={cardBg} p={8} borderRadius="md" minW="320px" boxShadow="lg">
                    <Heading size="md" mb={4}>{editingUser ? 'Edit User' : 'Add User'}</Heading>
                    <form onSubmit={handleUserFormSubmit}>
                      <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                          <FormLabel color={textColor}>Username</FormLabel>
                          <Input name="username" value={userForm.username} onChange={handleUserFormChange} autoFocus />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={textColor}>Email</FormLabel>
                          <Input name="email" value={userForm.email} onChange={handleUserFormChange} />
                        </FormControl>
                        <FormControl isRequired={!editingUser}>
                          <FormLabel color={textColor}>Password{editingUser ? ' (leave blank to keep unchanged)' : ''}</FormLabel>
                          <Input name="password" type="password" value={userForm.password} onChange={handleUserFormChange} autoComplete="new-password" />
                        </FormControl>
                        <FormControl>
                          <HStack>
                            <Switch
                              isChecked={userForm.is_admin}
                              onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                              colorScheme="purple"
                            />
                            <FormLabel color={textColor} mb={0}>Administrator</FormLabel>
                          </HStack>
                          <Text fontSize="xs" color={mutedColor} mt={1}>
                            Admin users have full access to all collections, documents, and settings
                          </Text>
                        </FormControl>
                        {userFormError && <Alert status="error"><AlertIcon />{userFormError}</Alert>}
                        <HStack justify="flex-end">
                          <Button onClick={handleUserFormClose} variant="ghost">Cancel</Button>
                          <Button colorScheme={themeConfig.colorScheme} type="submit" isLoading={userFormSaving}>{editingUser ? 'Save' : 'Add'}</Button>
                        </HStack>
                      </VStack>
                    </form>
                  </Box>
                </Box>
              )}
            </TabPanel>
            <TabPanel px={8}>
              {/* Collections Management Tab Content */}
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color={textColor}>Collections Management</Heading>
                      <Text fontSize="sm" color={mutedColor}>Create and manage document collections for better organization</Text>
                    </VStack>
                    <Button colorScheme={themeConfig.colorScheme} onClick={() => handleCollectionFormOpen()}>Add Collection</Button>
                  </HStack>
                </CardHeader>
                <CardBody>
                  {collections.length === 0 ? (
                    <Text color={mutedColor} textAlign="center" py={8}>No collections found.</Text>
                  ) : (
                    <Accordion allowToggle defaultIndex={collections.findIndex(c => c.isDefault)}>
                      {collections.map(collection => (
                        <AccordionItem 
                          key={collection.id}
                          border="1px"
                          borderColor={collection.isDefault ? `${themeConfig.colorScheme}.300` : borderColor}
                          borderRadius="md"
                          mb={4}
                          _last={{ mb: 0 }}
                        >
                          <h2>
                            <AccordionButton 
                              _expanded={{ 
                                bg: collection.isDefault ? selectedBg : hoverBg,
                                borderColor: collection.isDefault ? `${themeConfig.colorScheme}.500` : borderColor
                              }}
                              borderRadius="md"
                              p={4}
                            >
                              <Box flex="1" textAlign="left">
                                <HStack justify="space-between" align="center" w="full">
                                  <VStack align="start" spacing={1} flex={1}>
                                    <HStack>
                                      <Text fontWeight="bold" fontSize="lg">{collection.name}</Text>
                                      {collection.isDefault && (
                                        <Badge colorScheme={themeConfig.colorScheme} variant="solid">Default</Badge>
                                      )}
                                      {collection.isShared && (
                                        <Badge colorScheme="green" variant="outline">Shared</Badge>
                                      )}
                                    </HStack>
                                    <HStack spacing={4}>
                                      {collection.description && (
                                        <Text fontSize="sm" color={mutedColor}>{collection.description}</Text>
                                      )}
                                      <Text fontSize="xs" color={mutedColor}>
                                        {collection.documentIds.length} document{collection.documentIds.length !== 1 ? 's' : ''}
                                      </Text>
                                    </HStack>
                                  </VStack>
                                  <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
                                    <Tooltip label="Edit">
                                      <IconButton 
                                        aria-label="Edit collection" 
                                        icon={<EditIcon />} 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCollectionFormOpen(collection);
                                        }} 
                                      />
                                    </Tooltip>
                                    {collection.id !== 'all-documents' && (
                                      collection.isShared && collection.shareId ? (
                                        <>
                                          <Tooltip label="Copy Share Link">
                                            <IconButton 
                                              aria-label="Copy share link" 
                                              icon={<CopyIcon />} 
                                              size="sm" 
                                              colorScheme="green"
                                              variant="ghost"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                copyCollectionShareLink(collection);
                                              }} 
                                            />
                                          </Tooltip>
                                          <Tooltip label="Remove Share Link">
                                            <IconButton 
                                              aria-label="Remove share link" 
                                              icon={<LinkIcon />} 
                                              size="sm" 
                                              colorScheme="orange"
                                              variant="ghost"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveCollectionShareLink(collection);
                                              }} 
                                            />
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <Tooltip label="Generate Share Link">
                                          <IconButton 
                                            aria-label="Generate share link" 
                                            icon={<ExternalLinkIcon />} 
                                            size="sm" 
                                            colorScheme="blue"
                                            variant="ghost"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleGenerateCollectionShareLink(collection);
                                            }} 
                                          />
                                        </Tooltip>
                                      )
                                    )}
                                    {collection.id !== 'all-documents' && (
                                      <Tooltip label="Clone Collection">
                                        <IconButton 
                                          aria-label="Clone collection" 
                                          icon={<CloneIcon />} 
                                          size="sm" 
                                          colorScheme={themeConfig.colorScheme}
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCloneCollection(collection);
                                          }} 
                                        />
                                      </Tooltip>
                                    )}
                                    {collection.id !== 'all-documents' && (
                                      <Tooltip label="Delete">
                                        <IconButton 
                                          aria-label="Delete collection" 
                                          icon={<DeleteIcon />} 
                                          size="sm" 
                                          colorScheme="red"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCollection(collection);
                                          }} 
                                        />
                                      </Tooltip>
                                    )}
                                  </HStack>
                                </HStack>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={4} px={4}>
                            {collection.id === 'all-documents' ? (
                              /* All Documents - Single Column Layout */
                              <VStack align="stretch" spacing={4}>
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={3} color={textColor}>
                                    All Documents (Global Order):
                                  </Text>
                                  
                                  {/* Documents in collection (draggable for reordering) */}
                                  <VStack spacing={2} align="stretch">
                                    {collection.documentIds.map((docId, index) => {
                                      const config = configs.find(c => c.id === docId);
                                      if (!config) return null;
                                      
                                      return (
                                        <HStack 
                                          key={docId}
                                          p={3}
                                          borderRadius="md"
                                          bg={selectedBg}
                                          border="1px"
                                          borderColor={`${themeConfig.colorScheme}.300`}
                                          justify="space-between"
                                          draggable
                                          onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', index.toString());
                                            e.dataTransfer.setData('collectionId', collection.id);
                                          }}
                                          onDragOver={(e) => e.preventDefault()}
                                          onDrop={(e) => {
                                            e.preventDefault();
                                            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                            const collectionId = e.dataTransfer.getData('collectionId');
                                            if (collectionId === collection.id && fromIndex !== index) {
                                              try {
                                                moveDocument(fromIndex, index);
                                                toast({ title: 'Document order updated', status: 'success', duration: 2000, isClosable: true });
                                              } catch (e: any) {
                                                toast({ title: 'Failed to reorder documents', description: e.message, status: 'error', duration: 2000, isClosable: true });
                                              }
                                            }
                                          }}
                                          cursor="move"
                                          _hover={{ bg: hoverBg, transform: 'translateY(-1px)', boxShadow: 'sm' }}
                                          transition="all 0.2s"
                                        >
                                          <HStack spacing={3} flex={1} minW={0}>
                                            <DragHandleIcon color={mutedColor} />
                                            {config.icon ? (
                                              <Image src={config.icon} alt="icon" boxSize={5} />
                                            ) : (
                                              <Box w={5} h={5} bg={mutedColor} borderRadius="sm" />
                                            )}
                                            <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textColor}>
                                              {config.name}
                                            </Text>
                                          </HStack>
                                        </HStack>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              </VStack>
                            ) : (
                              /* Custom Collections - 3 Column Layout */
                              <Grid templateColumns="1fr 2fr" gap={6} alignItems="start">
                                {/* Left Column: Selected Documents */}
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={3} color={textColor}>
                                    Documents in this collection:
                                  </Text>
                                  
                                  <VStack spacing={2} align="stretch">
                                    {collection.documentIds.map((docId, index) => {
                                      const config = configs.find(c => c.id === docId);
                                      if (!config) return null;
                                      
                                      return (
                                        <HStack 
                                          key={docId}
                                          p={3}
                                          borderRadius="md"
                                          bg={selectedBg}
                                          border="1px"
                                          borderColor={`${themeConfig.colorScheme}.300`}
                                          justify="space-between"
                                          draggable
                                          onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', index.toString());
                                            e.dataTransfer.setData('collectionId', collection.id);
                                          }}
                                          onDragOver={(e) => e.preventDefault()}
                                          onDrop={(e) => {
                                            e.preventDefault();
                                            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                            const collectionId = e.dataTransfer.getData('collectionId');
                                            if (collectionId === collection.id && fromIndex !== index) {
                                              const reorderDocuments = async () => {
                                                try {
                                                  await CollectionService.reorderDocumentsInCollection(collection.id, fromIndex, index);
                                                  await loadCollections();
                                                  toast({ title: 'Document order updated', status: 'success', duration: 2000, isClosable: true });
                                                } catch (e: any) {
                                                  toast({ title: 'Failed to reorder documents', description: e.message, status: 'error', duration: 2000, isClosable: true });
                                                }
                                              };
                                              reorderDocuments();
                                            }
                                          }}
                                          cursor="move"
                                          _hover={{ bg: hoverBg, transform: 'translateY(-1px)', boxShadow: 'sm' }}
                                          transition="all 0.2s"
                                        >
                                          <HStack spacing={3} flex={1} minW={0}>
                                            <DragHandleIcon color={mutedColor} />
                                            {config.icon ? (
                                              <Image src={config.icon} alt="icon" boxSize={5} />
                                            ) : (
                                              <Box w={5} h={5} bg={mutedColor} borderRadius="sm" />
                                            )}
                                            <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textColor}>
                                              {config.name}
                                            </Text>
                                          </HStack>
                                          <IconButton
                                            aria-label="Remove from collection"
                                            icon={<DeleteIcon />}
                                            size="xs"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={() => handleToggleDocumentInCollection(collection.id, config.id, true)}
                                          />
                                        </HStack>
                                      );
                                    })}
                                    
                                    {collection.documentIds.length === 0 && (
                                      <Text fontSize="sm" color={mutedColor} fontStyle="italic" p={4} textAlign="center">
                                        No documents in this collection yet.
                                      </Text>
                                    )}
                                  </VStack>
                                </Box>

                                {/* Right Column: Available Documents */}
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={3} color={textColor}>
                                    Available documents to add:
                                  </Text>
                                  
                                  <SimpleGrid columns={2} spacing={2}>
                                    {configs.filter(config => !collection.documentIds.includes(config.id)).map(config => (
                                      <HStack 
                                        key={config.id}
                                        p={3}
                                        borderRadius="md"
                                        bg="transparent"
                                        border="1px"
                                        borderColor={borderColor}
                                        justify="space-between"
                                        _hover={{ bg: hoverBg, borderColor: `${themeConfig.colorScheme}.300` }}
                                        transition="all 0.2s"
                                        cursor="pointer"
                                        onClick={() => handleToggleDocumentInCollection(collection.id, config.id, false)}
                                      >
                                        <HStack spacing={2} flex={1} minW={0}>
                                          {config.icon ? (
                                            <Image src={config.icon} alt="icon" boxSize={4} />
                                          ) : (
                                            <Box w={4} h={4} bg={mutedColor} borderRadius="sm" />
                                          )}
                                          <Text fontSize="sm" fontWeight="medium" noOfLines={1} color={textColor}>
                                            {config.name}
                                          </Text>
                                        </HStack>
                                        <IconButton
                                          aria-label="Add to collection"
                                          icon={<AddIcon />}
                                          size="xs"
                                          colorScheme={themeConfig.colorScheme}
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleDocumentInCollection(collection.id, config.id, false);
                                          }}
                                        />
                                      </HStack>
                                    ))}
                                  </SimpleGrid>
                                  
                                  {configs.filter(config => !collection.documentIds.includes(config.id)).length === 0 && (
                                    <Text fontSize="sm" color={mutedColor} fontStyle="italic" p={4} textAlign="center">
                                      All documents are already in this collection.
                                    </Text>
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </CardBody>
              </Card>

              {/* Collection Add/Edit Modal */}
              {showCollectionForm && (
                <Box position="fixed" top={0} left={0} w="100vw" h="100vh" bg="blackAlpha.600" zIndex={1000} display="flex" alignItems="center" justifyContent="center">
                  <Box bg={cardBg} p={8} borderRadius="md" minW="400px" maxW="600px" boxShadow="lg">
                    <Heading size="md" mb={4}>{editingCollection ? 'Edit Collection' : 'Add Collection'}</Heading>
                    <form onSubmit={handleCollectionFormSubmit}>
                      <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                          <FormLabel color={textColor}>Name</FormLabel>
                          <Input 
                            value={collectionForm.name} 
                            onChange={(e) => setCollectionForm({...collectionForm, name: e.target.value})}
                            autoFocus 
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel color={textColor}>Description</FormLabel>
                          <Input 
                            value={collectionForm.description} 
                            onChange={(e) => setCollectionForm({...collectionForm, description: e.target.value})}
                          />
                        </FormControl>
                        <FormControl>
                          <HStack>
                            <Switch
                              isChecked={collectionForm.isDefault}
                              onChange={(e) => setCollectionForm({...collectionForm, isDefault: e.target.checked})}
                              colorScheme={themeConfig.colorScheme}
                            />
                            <FormLabel color={textColor} mb={0}>Set as default collection</FormLabel>
                          </HStack>
                        </FormControl>
                        
                        {/* Document Selection */}
                        <FormControl>
                          <FormLabel color={textColor}>Documents</FormLabel>
                          <Text fontSize="sm" color={mutedColor} mb={2}>Select documents to include in this collection:</Text>
                          <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto" border="1px" borderColor={borderColor} borderRadius="md" p={2}>
                            {configs.map(config => (
                              <HStack key={config.id} justify="space-between">
                                <HStack spacing={2} flex={1} minW={0}>
                                  {config.icon ? (
                                    <Image src={config.icon} alt="icon" boxSize={4} />
                                  ) : (
                                    <Box w={4} h={4} bg={mutedColor} borderRadius="sm" />
                                  )}
                                  <Text fontSize="sm" noOfLines={1}>{config.name}</Text>
                                </HStack>
                                <Switch
                                  isChecked={collectionForm.documentIds.includes(config.id)}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setCollectionForm(prev => ({
                                      ...prev,
                                      documentIds: isChecked
                                        ? [...prev.documentIds, config.id]
                                        : prev.documentIds.filter(id => id !== config.id)
                                    }));
                                  }}
                                  colorScheme={themeConfig.colorScheme}
                                  size="sm"
                                />
                              </HStack>
                            ))}
                          </VStack>
                        </FormControl>

                        {collectionFormError && <Alert status="error"><AlertIcon />{collectionFormError}</Alert>}
                        <HStack justify="flex-end">
                          <Button onClick={handleCollectionFormClose} variant="ghost">Cancel</Button>
                          <Button colorScheme={themeConfig.colorScheme} type="submit" isLoading={collectionFormSaving}>
                            {editingCollection ? 'Save' : 'Create'}
                          </Button>
                        </HStack>
                      </VStack>
                    </form>
                  </Box>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>

      </VStack>
    </Box>
  );
};